import React from 'react';
import {
  Box, Typography, IconButton, Tooltip, TextField, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon,
} from '@mui/icons-material';

const TRANSFORM_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'toString', label: 'toString' },
  { value: 'toInteger', label: 'toInteger' },
  { value: 'toUpperCase', label: 'toUpperCase' },
  { value: 'toLowerCase', label: 'toLowerCase' },
  { value: 'prefix', label: "prefix('...')" },
  { value: 'suffix', label: "suffix('...')" },
];

/**
 * Table-based field mapping editor.
 * Each row: source JSONPath → target field path → transform function
 *
 * Props:
 *   mappings: [{ source, target, transform }]
 *   onChange: (newMappings) => void
 *   colors: theme colors
 *   readOnly: boolean
 */
const FieldMappingEditor = ({ mappings = [], onChange, colors, readOnly = false }) => {

  const handleChange = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...mappings, { source: '', target: '', transform: '' }]);
  };

  const handleRemove = (index) => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  const handleMove = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= mappings.length) return;
    const updated = [...mappings];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const cellSx = { color: colors.text, borderBottom: `1px solid ${colors.border}`, py: 0.5, px: 1 };
  const headSx = { ...cellSx, color: colors.textSecondary, fontWeight: 600, fontSize: '0.75rem', bgcolor: colors.surface };
  const inputSx = {
    '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem', fontFamily: 'monospace', py: 0.5, px: 1 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          Field Mappings ({mappings.length})
        </Typography>
        {!readOnly && (
          <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}
            sx={{ textTransform: 'none', color: colors.accent, fontSize: '0.75rem' }}>
            Add Mapping
          </Button>
        )}
      </Box>
      <TableContainer sx={{ border: `1px solid ${colors.border}`, borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, width: 40 }}>#</TableCell>
              <TableCell sx={headSx}>Source Path (JSONPath)</TableCell>
              <TableCell sx={headSx}>Target Field</TableCell>
              <TableCell sx={{ ...headSx, width: 160 }}>Transform</TableCell>
              {!readOnly && <TableCell sx={{ ...headSx, width: 80 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => (
              <TableRow key={index} sx={{ '&:hover': { bgcolor: colors.hover || 'rgba(255,255,255,0.02)' } }}>
                <TableCell sx={cellSx}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>{index + 1}</Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    value={mapping.source || ''}
                    onChange={(e) => handleChange(index, 'source', e.target.value)}
                    placeholder="$.field.path"
                    disabled={readOnly}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    value={mapping.target || ''}
                    onChange={(e) => handleChange(index, 'target', e.target.value)}
                    placeholder="targetField"
                    disabled={readOnly}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  {mapping.transform && (mapping.transform.startsWith('prefix(') || mapping.transform.startsWith('suffix(')) ? (
                    <TextField
                      fullWidth size="small" variant="outlined"
                      value={mapping.transform || ''}
                      onChange={(e) => handleChange(index, 'transform', e.target.value)}
                      placeholder="prefix('...')"
                      disabled={readOnly}
                      sx={inputSx}
                    />
                  ) : (
                    <Select
                      fullWidth size="small"
                      value={mapping.transform || ''}
                      onChange={(e) => handleChange(index, 'transform', e.target.value)}
                      disabled={readOnly}
                      sx={{ ...inputSx, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' } }}
                    >
                      {TRANSFORM_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                </TableCell>
                {!readOnly && (
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <Tooltip title="Move up">
                        <IconButton size="small" onClick={() => handleMove(index, -1)} disabled={index === 0}
                          sx={{ p: 0.25, color: colors.textMuted }}>
                          <UpIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <IconButton size="small" onClick={() => handleMove(index, 1)} disabled={index === mappings.length - 1}
                          sx={{ p: 0.25, color: colors.textMuted }}>
                          <DownIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemove(index)}
                          sx={{ p: 0.25, color: colors.textMuted, '&:hover': { color: colors.error } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {mappings.length === 0 && (
              <TableRow>
                <TableCell colSpan={readOnly ? 4 : 5} sx={{ ...cellSx, textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    No field mappings defined. {!readOnly && 'Click "Add Mapping" to start.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FieldMappingEditor;
